/**
 * What: Classifies direct StudyRun results for Shopify report polling.
 * Transport / data-flow: Pure helpers over the shared direct results operation run metadata.
 *
 */

export type StudyRunStatusClass =
  | 'none'
  | 'running'
  | 'needs-attention'
  | 'stopped'
  | 'superseded'
  | 'results-ready'
  | 'completed-unpublished'
  | 'unknown'

export const REPORT_READY_STEP_KEY = 'promote-canonical-artifacts'

const NEEDS_ATTENTION_RUN_STATUSES = new Set(['failed', 'error'])
const ACTIVE_RUN_STATUSES = new Set(['queued', 'running'])
const STOPPED_RUN_STATUSES = new Set(['cancelled', 'canceled'])

/** Why: Report readiness depends on both run status and the final artifact-promotion step. */
export function isStudyRunReportReady(latestRunStatus: unknown, latestStepKey: unknown, reportReady?: unknown) {
  return (
    latestRunStatus === 'completed' &&
    (reportReady === true || (reportReady !== false && latestStepKey === REPORT_READY_STEP_KEY))
  )
}

/** Why: Shared classification prevents polling, card badges, and lifecycle filters from drifting. */
export function classifyStudyRunStatus(
  latestRunStatus: unknown,
  latestStepKey: unknown,
  reportReady?: unknown,
): StudyRunStatusClass {
  if (isStudyRunReportReady(latestRunStatus, latestStepKey, reportReady)) return 'results-ready'
  if (typeof latestRunStatus !== 'string' || latestRunStatus.length === 0) return 'none'
  if (NEEDS_ATTENTION_RUN_STATUSES.has(latestRunStatus)) return 'needs-attention'
  if (latestRunStatus === 'superseded') return 'superseded'
  if (STOPPED_RUN_STATUSES.has(latestRunStatus)) return 'stopped'
  if (ACTIVE_RUN_STATUSES.has(latestRunStatus)) return 'running'
  if (latestRunStatus === 'completed') return 'completed-unpublished'
  // Unknown or incomplete terminal states must remain visible as unavailable.
  return 'unknown'
}
